import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import { AlertService } from '../../../../services/alert.service';
import { UsersService } from '../../../../services/users.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import useInputValidation from '../../../../hooks/use-input-validation.hook';

declare const KTComponents: any;

interface Props {
  onCancel: () => void;
}

interface FormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const CRITERIA_LABELS = [
  'Carácter especial (!@#$%...)',
  'Al menos un número',
  'Al menos una mayúscula',
  'Al menos una minúscula',
  '10 caracteres exactos'
];

const ProgressItem = ({ active, label }: { active: boolean; label: string }) => (
  <div className="d-flex align-items-center mb-1">
    <div className={`bullet bullet-dot h-7px w-7px rounded bg-${active ? 'primary' : 'danger'}`} />
    <span className={`ms-2 fw-bolder text-${active ? 'primary' : 'dark'} small`}>{label}</span>
  </div>
);

const validateCriteria = (password: string) => [
  /[!@#$%¡.¿/&*()_+{}<>?]/.test(password),
  /[0-9]/.test(password),
  /[A-Z]/.test(password),
  /[a-z]/.test(password),
  password.length === 10
];

function ChangePasswordForm({ onCancel }: Props) {
  const navigate = useNavigate();
  const { passwordRegex, handleEightChars } = useInputValidation();
  const [progress, setProgress] = useState([false, false, false, false, false]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isSubmitting }
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' }
  });

  const newPasswordValue = watch('newPassword') || '';
  const confirmPattern = new RegExp(`^${newPasswordValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`);

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\s/g, '');
    setValue('newPassword', val, { shouldValidate: true });
    setProgress(validateCriteria(val));
    trigger('confirmPassword');
  };

  const handleNoSpacePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text');
    if (/\s/.test(pasted)) {
      AlertService.toastInfo('No se permiten espacios en la contraseña');
      e.preventDefault();
    }
  };

  const consecutiveActive = progress.reduce((count, v) => (v ? count + 1 : count), 0);

  useEffect(() => {
    KTComponents.init();
  });

  const onSubmit = async (data: FormValues) => {
    const confirmed = await AlertService.warning(
      '¿Cambiar contraseña?',
      'Se cerrará la sesión en todos los dispositivos.',
      () => Promise.resolve(true)
    );
    if (!confirmed) return;

    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...payload } = data;
      await UsersService.changePassword(payload);
      AlertService.toastSuccess('Contraseña actualizada correctamente');
      LocalStorageService.clear();
      setTimeout(() => navigate('/auth/login'), 200);
    } catch (error: any) {
      AlertService.toastError(error.error || error.status || 'Error al cambiar contraseña');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="animate-slide">
      <div className="fv-row mb-6">
        <Input
          label="Contraseña actual"
          requiredIndicator="required"
          type="password"
          placeholder="Ingresa tu contraseña actual"
          errors={errors.currentPassword}
          {...register('currentPassword', { required: true })}
          onChange={(e: any) => {
            const val = e.target.value.replace(/\s/g, '');
            setValue('currentPassword', val, { shouldValidate: true });
          }}
          onPaste={handleNoSpacePaste}
        />
      </div>

      <div className="fv-row mb-3">
        <div className="bg-light-primary rounded-3 p-4 mb-4">
          <p className="mb-3 small fw-medium">La contraseña debe cumplir:</p>
          {CRITERIA_LABELS.map((label, i) => (
            <ProgressItem key={i} active={progress[i]} label={label} />
          ))}
        </div>
        <Input
          label="Nueva contraseña"
          requiredIndicator="required"
          type="password"
          placeholder="Ingresa tu nueva contraseña"
          errors={errors.newPassword}
          {...register('newPassword', {
            required: true,
            pattern: { value: passwordRegex, message: 'La contraseña no cumple los requisitos' }
          })}
          onChange={handleNewPasswordChange}
          onKeyDown={handleEightChars}
          onPaste={handleNoSpacePaste}
        />
        <div className="d-flex align-items-center mt-3 mb-2">
          {progress.map((active, i) => (
            <div
              key={i}
              className={`flex-grow-1 rounded h-5px ${i < progress.length - 1 ? 'me-2' : ''} ${
                i < consecutiveActive ? 'bg-primary' : 'bg-secondary'
              }`}
            />
          ))}
        </div>
      </div>

      <div className="fv-row mb-8">
        <Input
          label="Confirmar nueva contraseña"
          requiredIndicator="required"
          type="password"
          placeholder="Repite tu nueva contraseña"
          errors={errors.confirmPassword}
          {...register('confirmPassword', {
            required: true,
            pattern: { value: confirmPattern, message: 'Las contraseñas no coinciden' }
          })}
          onChange={(e: any) => {
            const val = e.target.value.replace(/\s/g, '');
            setValue('confirmPassword', val, { shouldValidate: true });
          }}
          onPaste={handleNoSpacePaste}
        />
      </div>

      <div className="d-flex justify-content-end gap-3">
        <button type="button" className="btn btn-light btn-sm px-5 fw-bold" onClick={onCancel}>
          Cancelar
        </button>
        <Button
          type="submit"
          className="btn btn-primary btn-sm px-5 fw-bold"
          disabled={!isValid}
          isLoading={isSubmitting}
        >
          Guardar
        </Button>
      </div>
    </form>
  );
}

export default ChangePasswordForm;
